

import { useState, useEffect } from "react";
import Modal from "@/app/components/Modal";
import { formatCurrencyFull } from "@/lib/data";
import { mastersApi } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";

interface Quotation {
  id: number;
  quotation_number: string;
  client?: { id: number; name: string };
  event_type: string;
  event_date: string;
  total: number;
}

interface ConvertToEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotation: Quotation | null;
  onConfirm: (data: ConvertEventData) => Promise<void>;
  isLoading?: boolean;
  error?: string;
}

export interface ConvertEventData {
  quotation_id: number;
  event_date: string;
  advance_payment: number;
  payment_method: string;
  payment_date: string;
  payment_proof?: File | null;
}

const DEFAULT_PAYMENT_METHODS = [
  { value: "Cash", label: "Cash" },
  { value: "Bank Transfer", label: "Bank Transfer" },
  { value: "Cheque", label: "Cheque" },
  { value: "UPI", label: "UPI" },
  { value: "Credit Card", label: "Credit Card" },
];

export default function ConvertToEventModal({
  isOpen,
  onClose,
  quotation,
  onConfirm,
  isLoading = false,
  error = "",
}: ConvertToEventModalProps) {
  const [eventDate, setEventDate] = useState("");
  const [advancePayment, setAdvancePayment] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentMethods, setPaymentMethods] = useState(DEFAULT_PAYMENT_METHODS);
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [validationError, setValidationError] = useState("");

  // Format date to YYYY-MM-DD format for HTML date input (uses local timezone)
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    } catch {
      return "";
    }
  };

  const todayLocal = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isOpen && quotation) {
      const formattedEventDate = formatDateForInput(quotation.event_date);
      setEventDate(formattedEventDate);
      setPaymentDate(todayLocal());
      setAdvancePayment("");
      setPaymentMethod("bank_transfer");
      setPaymentProof(null);
      setValidationError("");
    }
  }, [isOpen, quotation]);

  useEffect(() => {
    mastersApi.paymentMethods.list({ status: 'active' })
      .then((res) => {
        const data = res.data?.data || res.data || [];
        if (data.length > 0) {
          setPaymentMethods(data.map((m: any) => ({ value: m.name, label: m.name })));
          setPaymentMethod(data[0].name);
        }
      })
      .catch(() => {
        setPaymentMethods(DEFAULT_PAYMENT_METHODS);
      });
  }, []);

  const handleConfirm = async () => {
    setValidationError("");

    if (!eventDate) {
      setValidationError("Event date is required");
      return;
    }

    if (!advancePayment || parseFloat(advancePayment) <= 0) {
      setValidationError("Advance payment amount must be greater than 0");
      return;
    }

    if (!paymentMethod) {
      setValidationError("Payment method is required");
      return;
    }

    if (!paymentDate) {
      setValidationError("Payment date is required");
      return;
    }

    const advanceAmount = parseFloat(advancePayment);
    const totalAmount = quotation?.total || 0;

    // Validate advance payment cannot exceed total amount
    if (advanceAmount > totalAmount) {
      setValidationError(
        `Advance payment cannot exceed total amount (${formatCurrencyFull(totalAmount)})`
      );
      return;
    }

    try {
      await onConfirm({
        quotation_id: quotation?.id || 0,
        event_date: eventDate,
        advance_payment: advanceAmount,
        payment_method: paymentMethod,
        payment_date: paymentDate,
        payment_proof: paymentProof,
      });
    } catch (err) {
      console.error("Error confirming conversion:", err);
    }
  };

  if (!quotation) return null;

  const totalAmount = quotation.total || 0;
  const remainingAmount = totalAmount - (parseFloat(advancePayment) || 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Convert Quotation to Event"
      size="lg"
    >
      <div className="space-y-6">
        {/* Quotation Summary */}
        <div className="bg-white border border-stone-200 p-4 rounded-xl">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-stone-600 mb-1">
                Quotation #
              </p>
              <p className="text-sm font-semibold text-stone-800">
                {quotation.quotation_number || `#${quotation.id}`}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-stone-600 mb-1">Client</p>
              <p className="text-sm font-semibold text-stone-800">
                {quotation.client?.name || "-"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-stone-600 mb-1">
                Event Type
              </p>
              <p className="text-sm font-semibold text-stone-800">
                {quotation.event_type}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-stone-600 mb-1">
                Total Amount
              </p>
              <p className="text-sm font-semibold text-amber-700">
                {formatCurrencyFull(totalAmount)}
              </p>
            </div>
          </div>
        </div>

        {/* Event Date */}
        <div>
          <label className="block text-sm font-semibold text-stone-700 mb-2">
            Event Date
          </label>
          <input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-stone-300 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
          />
          <p className="text-xs text-stone-500 mt-2">
            This date will be used as the event date when creating the event.
          </p>
        </div>

        {/* Advance Payment Section */}
        <div className="border border-stone-200 rounded-xl p-4 space-y-4">
          <h3 className="font-semibold text-stone-800 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Advance Payment
          </h3>

          {/* Advance Payment Amount */}
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-2">
              Advance Amount
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-600 font-medium">
                ₹
              </span>
              <input
                type="number"
                value={advancePayment}
                onChange={(e) => setAdvancePayment(e.target.value)}
                placeholder="0"
                min="0"
                max={totalAmount}
                step="100"
                className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-stone-300 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
              />
            </div>
            <p className="text-xs text-stone-500 mt-2">
              Remaining: {formatCurrencyFull(Math.max(0, remainingAmount))}
            </p>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-2">
              Payment Method
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-stone-300 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm bg-white"
            >
              {paymentMethods.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-2">
              Payment Date
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-stone-300 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
            />
          </div>

          {/* Payment Proof Upload - Compressed */}
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-2">
              Payment Proof (Optional)
            </label>
            <p className="text-xs text-stone-500 mb-2">
              Upload receipt, screenshot, or document proving the payment (JPG, PNG, PDF)
            </p>
            <ImageUpload
              selectedFile={paymentProof}
              onFileSelect={(file) => setPaymentProof(file)}
              maxSize={2 * 1024 * 1024}
              compressToWebP={true}
            />
          </div>
        </div>

        {/* Error Message */}
        {(validationError || error) && (
          <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-sm flex items-start gap-2">
            <svg
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="font-medium">Error</p>
              <p>{validationError || error}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 font-semibold hover:bg-stone-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-emerald-700"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Converting...
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                Convert to Event
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
